import React, { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const onSubmit = (e) => {
    e.preventDefault();
    toast.success("Message sent!", { description: "We'll get back to you within 24 hours." });
    setForm({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <main className="max-w-[1200px] mx-auto px-4 md:px-8 py-10 md:py-16" data-testid="contact-page">
      <div className="text-center max-w-2xl mx-auto">
        <span className="text-[10px] md:text-[11px] font-bold tracking-[0.24em] md:tracking-[0.26em] uppercase text-[#FF5A1F]">Get in touch</span>
        <h1 className="mt-3 md:mt-4 font-display text-3xl sm:text-4xl md:text-6xl font-black leading-tight">
          We'd love to hear from you.
        </h1>
        <p className="mt-3 md:mt-4 text-sm md:text-base text-neutral-600">
          Questions, feedback, bulk orders or collaborations — drop us a line.
        </p>
      </div>

      <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-10 mt-8 md:mt-14">
        <div className="space-y-5 md:space-y-6">
          <Info icon={<Mail className="w-5 h-5" />} title="Email" value="hello@stealodeal.in" sub="We reply within 24 hours." />
          <Info icon={<Phone className="w-5 h-5" />} title="Phone / WhatsApp" value="+91 99998 37145" sub="Mon–Sat · 10am – 7pm IST" />
          <Info icon={<MapPin className="w-5 h-5" />} title="Head Office" value="Devan & Co." sub="New Delhi, India" />

          <div className="rounded-3xl bg-neutral-900 text-white p-5 md:p-6">
            <div className="text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase text-[#FF5A1F]">Bulk / Corporate</div>
            <p className="mt-2 text-xs md:text-sm">
              Looking to gift your team? We do custom laser-engraved card holders and keychains starting at 25 pieces.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="bg-neutral-50 rounded-3xl p-5 md:p-10" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Full name" value={form.name} set={(v) => setForm({ ...form, name: v })} required testid="contact-name" />
            <Field label="Email" type="email" value={form.email} set={(v) => setForm({ ...form, email: v })} required testid="contact-email" />
          </div>
          <Field label="Subject" value={form.subject} set={(v) => setForm({ ...form, subject: v })} required testid="contact-subject" />
          <div className="mt-4">
            <label className="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-600">Message</label>
            <textarea
              required
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={6}
              className="mt-2 w-full rounded-2xl border border-neutral-300 bg-white px-4 py-3 text-sm focus:outline-none focus:border-neutral-900"
              placeholder="Tell us how we can help..."
              data-testid="contact-message"
            />
          </div>
          <button
            className="mt-6 h-12 px-7 rounded-full bg-[#FF5A1F] hover:bg-neutral-900 text-white font-bold text-sm flex items-center gap-2 transition"
            data-testid="contact-submit"
          >
            Send Message <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </main>
  );
}

function Info({ icon, title, value, sub }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-full bg-[#FF5A1F]/10 text-[#FF5A1F] flex items-center justify-center flex-shrink-0">{icon}</div>
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500">{title}</div>
        <div className="mt-1 font-semibold text-neutral-900">{value}</div>
        <div className="text-sm text-neutral-500">{sub}</div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, set, required, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-600">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => set(e.target.value)}
        className="mt-2 w-full h-12 rounded-full border border-neutral-300 bg-white px-5 text-sm focus:outline-none focus:border-neutral-900"
        data-testid={testid}
      />
    </div>
  );
}
